import React, {ReactNode} from 'react'
import {ColumnProps, ISorting} from 'models/Common'
import KTPagination from './KTPagination'
import {useTranslation} from 'react-i18next'

type Props<T> = {
 columns: Array<ColumnProps<T>>
 data?: T[]
 headerClass?: string
 headerRowClass?: string
 headerColClass?: string
 rowClass?: string
 colClass?: string
 tableClass?: string
 showPagination?: boolean
 currentPage?: number
 itemsPerPage?: number
 totalRecord?: number
 sortConfig?: ISorting
 onPageChange?: (pageNo: number) => void
 setSortConfig?: (newState: ISorting) => void
 setItemsPerPage?: (pageSize: number) => void
 getRowId?: (row: T) => string | number
 expandedRowIds?: Set<string | number>
 renderExpandedRow?: (row: T, index: number) => ReactNode
}

const KTTable = <T,>({
 data,
 columns,
 headerClass,
 rowClass,
 colClass,
 tableClass,
 headerColClass,
 headerRowClass,
 showPagination,
 currentPage,
 itemsPerPage,
 totalRecord,
 sortConfig,
 setSortConfig,
 onPageChange,
 setItemsPerPage,
 getRowId,
 expandedRowIds,
 renderExpandedRow,
}: Props<T>) => {
 const {t} = useTranslation()
 const handleSort = (key: string) => {
  if (sortConfig) {
   let direction = 'asc'
   if (
    sortConfig.key === key &&
    sortConfig.direction === 'asc'
   ) {
    direction = 'desc'
   }
   if (setSortConfig) setSortConfig({key, direction})
  }
 }
 const headers = columns.map((column, index) => {
  return (
   <th
    key={`headCell-${index}`}
    className={`${headerColClass} ${
     column.className
    } border-[#E6EBF0] border-[0.5px] text-white  py-3 px-2 ${
     index === 0 && '!border-l-[#80868C]'
    } ${
     columns.length - 1 === index && '!border-r-[#80868C]'
    }`}
   >
    <div className='flex justify-between items-center'>
     <span>{column.title}</span>
     {sortConfig && column?.sort && (
      <span
       className='cursor-pointer sort-icon px-1 text-xs font-normal w-5'
       onClick={() => handleSort(column.key)}
      >
       {sortConfig.key === column.key ? (
        <span className=''>
         {sortConfig.direction === 'asc' ? (
          <i
           className='fa fa-long-arrow-up'
           aria-hidden='true'
          ></i>
         ) : (
          <i
           className='fa fa-long-arrow-down'
           aria-hidden='true'
          ></i>
         )}
        </span>
       ) : (
        <span className=''>
         <i
          className='fa fa-long-arrow-up'
          aria-hidden='true'
         ></i>
         <i
          className='fa fa-long-arrow-down'
          aria-hidden='true'
         ></i>
        </span>
       )}
      </span>
     )}
    </div>
   </th>
  )
 })

 const stripeBgClass = rowClass?.match(/even:(\S+)/)?.[1]
 const useDataIndexStriping = !!(
  renderExpandedRow && stripeBgClass
 )

 const rows = !data?.length ? (
  <tr>
   <td colSpan={columns.length} className='text-center'>
    {t('pagination.noData')}
   </td>
  </tr>
 ) : (
  data?.map((row, index) => {
   const rowId = getRowId ? getRowId(row as T) : index
   const isExpanded =
    expandedRowIds?.has(rowId) && renderExpandedRow
   const isEvenDataRow = index % 2 === 1
   const rowBackgroundClass = useDataIndexStriping
    ? isEvenDataRow
      ? stripeBgClass
      : 'bg-white'
    : 'bg-white'
   const resolvedRowClass = useDataIndexStriping
    ? `${
       rowClass?.replace(/even:\S+/g, '').trim() ?? ''
      } ${rowBackgroundClass} text-[#101010]`.trim()
    : `${rowClass} bg-white text-[#101010]`

   return (
    <React.Fragment key={`row-${rowId}`}>
     <tr className={resolvedRowClass}>
      {columns.map((column, index2) => {
       const value = column.isSrNo
        ? ((currentPage ? currentPage : 1) - 1) *
           (itemsPerPage ? itemsPerPage : 10) +
          ++index
        : column.render
        ? column.render(column, row as T, index)
        : (row[column.key as keyof typeof row] as string)

       return (
        <td
         className={`${colClass} ${column.className} border-[#E6EBF0] border-[0.5px] text-[#101010] px-2 py-2`}
         key={`cell-${index2}`}
        >
         {value}
        </td>
       )
      })}
     </tr>
     {isExpanded && (
      <tr
       key={`expanded-${rowId}`}
       className={
        useDataIndexStriping
         ? rowBackgroundClass
         : undefined
       }
      >
       <td
        colSpan={columns.length}
        className='!p-0 !border-0'
       >
        {renderExpandedRow(row as T, index)}
       </td>
      </tr>
     )}
    </React.Fragment>
   )
  })
 )

 return (
  <div className='flex flex-col'>
   <div className='overflow-x-auto'>
    <div className='inline-block min-w-full'>
     <div className='overflow-hidden'>
      <table className={`${tableClass} table w-full`}>
       <thead
        className={`${headerClass} bg-[#80868C] text-black text-sm font-semibold`}
       >
        <tr
         className={`${headerRowClass} bg-[#80868C] text-black`}
        >
         {headers}
        </tr>
       </thead>

       <tbody>{rows}</tbody>
      </table>
     </div>
    </div>
   </div>
   {showPagination && (
    <KTPagination
     currentPage={currentPage}
     itemsPerPage={itemsPerPage}
     totalRecord={totalRecord}
     onPageChange={onPageChange}
     setItemsPerPage={setItemsPerPage}
    />
   )}
  </div>
 )
}

export default KTTable
